import { Box, DialogActions, DialogContent, styled } from '@mui/material';
import { DialogDefault, InfoTooltip, ThemeButton, ThemeTypography } from 'DesignSystem';
import useDatasetMutation from 'FeatureModules/DataImport/hooks/useDatasetMutation';
import {
  datasetImportSchema,
  IDatasetFarmClusteringFormValues,
  IDatasetImportFormValues,
} from 'FeatureModules/DataImport/schemas/geoUpload.schema';
import { EDatasetColumn } from 'FeatureModules/DataImport/types/dataset.types';
import { Field, Form, Formik } from 'formik';
import { Switch } from 'formik-mui';
import { IDatasetMappedColumn } from 'graphql/secure/mutations/types/dataset-mutation.types';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import React, { FC } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';

interface IConfirmDatasetImportDialogProps extends IDefaultDialogProps {
  dataset: {
    datasetId: string;
    mappedColumns?: IDatasetMappedColumn[];
    countryCode: string;
    rawMaterialId: string;
  };
  values: Partial<IDatasetImportFormValues & IDatasetFarmClusteringFormValues>;
  onProcessingStart: () => void;
  onProcessingComplete: (processedDatasetId?: string) => void;
}

const FormContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.custom.themeColors.grayScale[5],
  padding: theme.spacing(2),
}));

const FormItemContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  width: 'fit-content',
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.custom.themeColors.white,
  padding: theme.spacing(0.5, 1),
}));

const StyledTooltip = styled(InfoTooltip)(() => ({
  margin: 0,
}));

const ConfirmDatasetImportDialog: FC<IConfirmDatasetImportDialogProps> = ({
  open,
  values: existingValues,
  dataset: { datasetId, mappedColumns, countryCode, rawMaterialId },
  onProcessingComplete,
  onProcessingStart,
  onClose,
}) => {
  const { openDialog } = useDialog();
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { startDatasetProcessing: startDatasetsProcessing } = useDatasetMutation();

  const initialValues = {
    ...datasetImportSchema.default(),
    ...existingValues,
  };

  const containsSiteClusterIdColumn = mappedColumns?.some(
    column => column.targetColumnName === EDatasetColumn.ORIGIN_CLUSTER_ID
  );

  const handleSubmit = async (
    values: IDatasetImportFormValues & Partial<IDatasetFarmClusteringFormValues>
  ) => {
    try {
      if (containsSiteClusterIdColumn) {
        onProcessingStart();
        // Start the processing of the dataset again to validate the new data
        const processingResponse = await startDatasetsProcessing({
          datasetId,
          mappedColumns,
          csvSeperator: ';',
          overwriteExisting: values.overwriteExisting,
        });

        if (processingResponse.errors?.length) {
          setErrorMessage(processingResponse.errors.map(err => err.message).join(' '));
          onProcessingComplete(undefined);
          return;
        }

        if (processingResponse.data?.processDataset.dataset) {
          setSuccessMessage(
            'Farm site(s) have been created or updated and are now also available in your Partners and Sites library.'
          );
        }
        onProcessingComplete(processingResponse.data?.processDataset.dataset.id);
      } else {
        // Only show the farm clustering dialog if the imported datasets does not contain the origin cluster id column
        openDialog({
          type: 'CONFIRM_FARM_CLUSTERING',
          props: {
            values,
            dataset: { datasetId, mappedColumns, countryCode, rawMaterialId },
            onProcessingComplete,
            onProcessingStart,
          },
        });
      }
    } catch (error) {
      onProcessingComplete(undefined);
      // eslint-disable-next-line no-console
      console.error('Something went wrong updating / validating the dataset');
      setErrorMessage(
        'Something went wrong validating the dataset. If the issue persists please contact support.'
      );
    } finally {
      onClose?.();
    }
  };

  return (
    <DialogDefault open={open} title="Confirm Import settings" onClose={onClose}>
      <Formik
        initialValues={initialValues}
        validationSchema={datasetImportSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2}>
                <ThemeTypography variant="BODY_MEDIUM_BOLD">
                  Some plots & farms may already exist in the platform (sharing the same ID). Would
                  you like to update and overwrite the existing farm and plot data with the imported
                  data?
                </ThemeTypography>
                <FormContainer>
                  <FormItemContainer>
                    <ThemeTypography variant="BODY_MEDIUM">
                      Import and update farm and plot data for existing IDs:
                    </ThemeTypography>
                    <Field name="overwriteExisting" fullWidth component={Switch} type="checkbox" />
                    <StyledTooltip
                      variant="INFO"
                      text="Enable this to update the existing Farm data with the new imported Farm data for the Farm ids that already exist in the platform. The old Farm data will be kept as history in the platform and any changes in the imported data will become the new current status for this Farm."
                    />
                  </FormItemContainer>
                  <ThemeTypography variant="BODY_MEDIUM" color="GRAY_60">
                    Plot and farm data for existing IDs will be updated. Any overwritten plot data
                    will still be available in the farm & plot history.
                  </ThemeTypography>
                </FormContainer>
              </Box>
            </DialogContent>

            <DialogActions>
              <Box display="flex" justifyContent="space-between" width="100%">
                <ThemeButton color="BLUE_ICE" onClick={onClose}>
                  Cancel
                </ThemeButton>
                <ThemeButton type="submit" loading={isSubmitting}>
                  Confirm
                </ThemeButton>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </DialogDefault>
  );
};

export default ConfirmDatasetImportDialog;
